<template>
  <AuthGuard>
    <v-container class="w-full">
      <PageHeader :title="titleMap[props.property]" :showBack="true" back-to="/customers">
        <v-btn
          prepend-icon="mdi-content-save-check-outline"
          @click="handleCLickSave"
          :loading="isLoading"
          :disabled="_.isEmpty(text)"
          color="success"
        >
          Save
        </v-btn>
      </PageHeader>

      <v-row>
        <v-col>
          <Codemirror
            v-model="text"
            placeholder=""
            :style="{ height: '100%' }"
            :autofocus="true"
            :indent-with-tab="true"
            :tab-size="2"
            :extensions="[Html()]"
          />
        </v-col>
        <v-col v-if="props.property !== 'reportPrompt'">
          <div v-html="html"></div>
        </v-col>
      </v-row>
    </v-container>
  </AuthGuard>
</template>

<script setup lang="ts">
import _ from "lodash";
import { computed, onMounted, reactive, ref } from "vue";
import AuthGuard from "@/components/Auth/AuthGuard.vue";
import { useGlobalStore } from "@/stores/global";
import { convertMarkdownToHTML, makeAlert } from "@/utils";
import type { IApp } from "@/types";
import { html as Html } from "@codemirror/lang-html";
import { Codemirror } from "vue-codemirror";
import { useReportStore } from "@/stores/report";
import PageHeader from "@/components/PageHeader.vue";

const state = reactive({
  loading: false
});

const titleMap: Record<keyof IApp.Content, string> = {
  privacyPolicy: "Privacy Policy",
  termsOfService: "Terms of Service",
  reportPrompt: "Report Summary Generation Prompt"
};

const props = defineProps<{
  property: keyof IApp.Content;
}>();

const globalStore = useGlobalStore();
const reportStore = useReportStore();

const text = ref<string>(
  props.property === "reportPrompt"
    ? reportStore.getPrompt()
    : globalStore.contents[props.property] || ""
);
const html = computed(() => convertMarkdownToHTML(text.value));
const isLoading = computed(() => globalStore.isContentsLoading || state.loading);

const handleCLickSave = async () => {
  if (_.isEmpty(text.value)) {
    makeAlert("Content is empty", "error");
    return;
  }
  state.loading = true;
  if (props.property === "reportPrompt") {
    reportStore.updatePrompt(text.value);
    await reportStore.savePrompt();
  } else {
    await globalStore.upsertContents({
      [props.property]: text.value
    });
  }
  state.loading = false;
};

onMounted(() => {
  if (props.property === "reportPrompt") {
    if (_.isEmpty(reportStore.getPrompt())) {
      state.loading = true;
      reportStore.fetchPrompt().then((value) => {
        text.value = value;
        state.loading = false;
      });
    }
  }
});
</script>
